// src/components/admin/AdminLayout.js
import React, { useState, Suspense } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FaBars,
  FaTachometerAlt,
  FaUsers,
  FaProjectDiagram,
  FaCalendar,
  FaChartBar,
  FaNewspaper,
  FaBuilding,
  FaImages,
  FaCog,
  FaUserCircle,
  FaInfoCircle,
  FaHandshake,
  FaGavel,
  FaQuestionCircle,
  FaFileAlt,
  FaHeadset,
  FaEnvelope,
  FaInbox,
  FaFolder,
  FaTags,
  FaComments,
  FaQuestionCircle as FaQuestionMark,
  FaClipboardList,
  FaRobot,
  FaMagic,
  FaMemory,
  FaAirFreshener
} from 'react-icons/fa';
import Navbar from './Navbar';
import Footer from './Footer';
import ErrorBoundary from '../common/ErrorBoundary';
import LoadingSpinner from '../common/LoadingSpinner';

const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { path: '/admin/dashboard', icon: FaTachometerAlt, label: 'Dashboard' },
    {
      path: '/admin/ai',
      icon: FaRobot,
      label: 'AI Tools',
      subItems: [
        { path: '/admin/llms', icon: FaRobot, label: 'LLMs' },
        { path: '/admin/prompts', icon: FaMagic, label: 'Prompts' },
        { path: '/admin/content-generator', icon: FaFileAlt, label: 'AI Content Generator' },
        { path: '/admin/n8n', icon: FaProjectDiagram, label: 'n8n Workflows' },
      ]
    },
    {
      path: '/admin/ai-orchestrator',
      icon: FaAirFreshener,
      label: 'AI Orchestration',
      subItems: [
        { path: '/admin/ai-orchestrator/dashboard', icon: FaTachometerAlt, label: 'Dashboard' },
        { path: '/admin/ai-orchestrator/agents', icon: FaRobot, label: 'Agents' },
        { path: '/admin/ai-orchestrator/tasks', icon: FaClipboardList, label: 'Task Queue' },
        { path: '/admin/ai-orchestrator/memory', icon: FaMemory, label: 'Memory' },
        { path: '/admin/ai-orchestrator/stats', icon: FaChartBar, label: 'Statistics' },
        { path: '/admin/ai-orchestrator/settings', icon: FaCog, label: 'Settings' }
      ]
    },
    {
      path: '/admin/content-management',
      icon: FaNewspaper,
      label: 'Content Management',
      subItems: [
        { path: '/admin/blogs', icon: FaNewspaper, label: 'Blogs' },
        { path: '/admin/case-studies', icon: FaProjectDiagram, label: 'Case Studies' },
        { path: '/admin/forums', icon: FaQuestionMark, label: 'Forums' },
        { path: '/admin/projects', icon: FaProjectDiagram, label: 'Projects' },
        { path: '/admin/comments', icon: FaComments, label: 'Comments' },
        { path: '/admin/categories', icon: FaFolder, label: 'Categories' },
        { path: '/admin/tags', icon: FaTags, label: 'Tags' }
      ]
    },
    {
      path: '/admin/communication',
      icon: FaEnvelope,
      label: 'Communication',
      subItems: [
        { path: '/admin/newsletter', icon: FaEnvelope, label: 'Newsletter' },
        { path: '/admin/contact', icon: FaInbox, label: 'Contact Messages' },
        { path: '/admin/calendar', icon: FaCalendar, label: 'Calendar' }
      ]
    },
    {
      path: '/admin/analytics',
      icon: FaChartBar,
      label: 'Analytics & Reports',
      subItems: [
        { path: '/admin/reports', icon: FaChartBar, label: 'Reports' },
        { path: '/admin/system-logs', icon: FaClipboardList, label: 'System Logs' }
      ]
    },
    {
      path: '/admin/settings',
      icon: FaCog,
      label: 'Settings',
      subItems: [
        { path: '/admin/profile', icon: FaUserCircle, label: 'My Profile' },
        { path: '/admin/users', icon: FaUsers, label: 'Users' },
        { path: '/admin/media', icon: FaImages, label: 'Media Library' },
        { path: '/admin/services', icon: FaFileAlt, label: 'Services' }
      ]
    },
    {
      path: '/admin/company',
      icon: FaBuilding,
      label: 'Company',
      subItems: [
        { path: '/admin/company/settings', icon: FaCog, label: 'Settings' },
        { path: '/admin/company/contact-info', icon: FaUserCircle, label: 'Contact Info' },
        { path: '/admin/company/who-we-are', icon: FaInfoCircle, label: 'Who We Are' },
        { path: '/admin/company/services', icon: FaHandshake, label: 'Services' },
        { path: '/admin/company/social', icon: FaUserCircle, label: 'Social Links' },
        { path: '/admin/company/description', icon: FaQuestionCircle, label: 'Description' }
      ]
    },
    {
      path: '/admin/content',
      icon: FaFileAlt,
      label: 'Content Pages',
      subItems: [
        {
          path: '/admin/content/legal',
          icon: FaGavel,
          label: 'Legal',
          subLabel: 'Privacy Policy, Terms, etc.'
        },
        {
          path: '/admin/content/support',
          icon: FaHeadset,
          label: 'Support',
          subLabel: 'Help Center, FAQs, etc.'
        },
        {
          path: '/admin/content/services',
          icon: FaHandshake,
          label: 'Services',
          subLabel: 'Service Details, Features, etc.'
        },
        {
          path: '/admin/content/projects',
          icon: FaProjectDiagram,
          label: 'Projects',
          subLabel: 'Case Studies, Portfolio, etc.'
        }
      ]
    }
  ];

  const MenuItem = ({ item }) => {
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const Icon = item.icon;
    const isActive = location.pathname.startsWith(item.path);

    // Auto-expand submenu if current path matches
    React.useEffect(() => {
      if (isActive && item.subItems) {
        setIsSubMenuOpen(true);
      }
    }, [location.pathname, isActive, item.subItems]);

    if (item.subItems) {
      return (
        <li className={`${isActive ? 'bg-gray-700' : ''}`}>
          <button
            onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
            className="w-full flex items-center p-4 hover:bg-gray-700 text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white cursor-pointer"
            aria-expanded={isSubMenuOpen}
            aria-controls={`submenu-${item.path}`}
          >
            <Icon className="flex-shrink-0" aria-hidden="true" />
            {isSidebarOpen && (
              <>
                <span className="ml-4">{item.label}</span>
                <span 
                  className={`ml-auto transform transition-transform duration-200 ${isSubMenuOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                >
                  ▼
                </span>
              </>
            )}
          </button>
          {isSubMenuOpen && isSidebarOpen && (
            <ul 
              id={`submenu-${item.path}`}
              className="bg-gray-900"
              role="menu"
              aria-label={`${item.label} submenu`}
            >
              {item.subItems.map((subItem, index) => {
                const SubIcon = subItem.icon;
                const isSubActive = location.pathname === subItem.path;
                return (
                  <li key={index} role="none">
                    <NavLink
                      to={subItem.path}
                      className={({ isActive }) => `
                        flex items-center p-4 pl-8 hover:bg-gray-700 cursor-pointer
                        ${isActive || isSubActive ? 'bg-gray-700 text-white' : 'text-gray-400'}
                        focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white
                      `}
                      role="menuitem"
                    >
                      <SubIcon className="flex-shrink-0" aria-hidden="true" />
                      <span className="ml-4">
                        {subItem.label}
                        {subItem.subLabel && (
                          <span className="block text-xs text-gray-500">{subItem.subLabel}</span>
                        )}
                      </span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li role="none">
        <NavLink
          to={item.path}
          className={({ isActive }) => `
            flex items-center p-4 hover:bg-gray-700 cursor-pointer
            ${isActive ? 'bg-gray-700 text-white' : 'text-gray-300'}
            focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white
          `}
          role="menuitem"
        >
          <Icon className="flex-shrink-0" aria-hidden="true" />
          {isSidebarOpen && <span className="ml-4">{item.label}</span>}
        </NavLink>
      </li>
    );
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar */}
      <nav 
        className={`${isSidebarOpen ? 'w-64' : 'w-16'} bg-gray-800 transition-all duration-300`}
        role="navigation"
        aria-label="Admin navigation"
      >
        <div className="p-4 flex justify-between items-center">
          <h2 className={`text-xl font-semibold text-white ${isSidebarOpen ? 'block' : 'hidden'}`}>
            Admin
          </h2>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white cursor-pointer"
            aria-expanded={isSidebarOpen}
            aria-controls="admin-sidebar"
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <FaBars size={20} aria-hidden="true" />
          </button>
        </div>
        <ul 
          id="admin-sidebar"
          className="space-y-1"
          role="menu"
          aria-label="Admin menu"
        >
          {menuItems.map((item, index) => (
            <MenuItem key={index} item={item} />
          ))}
        </ul>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <Navbar />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              {children}
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default AdminLayout;
